import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

/**
 * One URL-mirrored value. Params are stored JSON-encoded, so an object round-trips
 * as faithfully as a primitive — filter pickers keep their `{ label, value }`
 * option in the URL so a reload can repaint the chip without re-fetching.
 */
export type SearchParamValue =
  | string
  | number
  | boolean
  | string[]
  | Record<string, string | number | boolean | null | undefined>
  | null
  | undefined;

export type SearchParams = {
  [key: string]: SearchParamValue;
};

/**
 *
 * @param params - The search parameters to be formatted.
 * @description This function takes an object with search parameters and formats it.
 * It deencodes the values using atob and returns a new object with the formatted parameters parsed.
 *
 */
export const formatSearchParams = (params: Record<string, string>) => {
  const formattedParams: Record<string, string | number | boolean | null | undefined | string[]> =
    {};

  Object.entries(params).forEach(([key, value]) => {
    let val;
    try {
      val = value ? JSON.parse(decodeURIComponent(atob(value))) : undefined;
    } catch (e) {
      // Ignore invalid base64 or JSON values
      return;
    }
    formattedParams[key] = val === 'true' ? true : val === 'false' ? false : val;
  });
  return formattedParams;
};

function selectSearchParams(
  params: ReturnType<typeof formatSearchParams>,
  namespace: string
): ReturnType<typeof formatSearchParams> {
  const prefix = namespace ? `${namespace}:` : '';
  const selected: ReturnType<typeof formatSearchParams> = {};
  for (const [key, value] of Object.entries(params)) {
    if (namespace ? !key.startsWith(prefix) : key.includes(':')) continue;
    selected[key.slice(prefix.length)] = value;
  }
  return selected;
}

type SearchParamsUpdate = ((prev: SearchParams) => SearchParams) | SearchParams;

/**
 *
 * @description This hook is used to get and set the search parameters from/to the URL.
 * It returns an object with the search parameters as key-value pairs.
 *
 * Reads through React Router's `useLocation` and writes through `history.replace`,
 * so every consumer re-renders when the URL query changes — whether from a
 * `setSearchParams` call or from navigation. This keeps URL-mirrored state (e.g.
 * the global hotel selection) in sync across pages instead of silently going
 * stale after navigation, which a raw `window.history.replaceState` could not do.
 *
 * Namespaced keys use `namespace:key` on the wire. Unscoped consumers omit them,
 * so a sibling tab cannot treat another tab's state as its own filters.
 */
export const useSearchParams = ({ namespace = '' }: { namespace?: string } = {}): readonly [
  ReturnType<typeof formatSearchParams>,
  (newParams: SearchParamsUpdate) => void
] => {
  const history = useHistory();
  const { pathname, search, hash } = useLocation();

  const formattedSearchParams = useMemo(() => {
    const query = new URLSearchParams(search);
    return selectSearchParams(formatSearchParams(Object.fromEntries(query.entries())), namespace);
  }, [namespace, search]);

  const setSearchParams = useCallback(
    (newParams: SearchParamsUpdate) => {
      const params = new URLSearchParams(window.location.search);
      const currentParams = selectSearchParams(
        formatSearchParams(Object.fromEntries(params.entries())),
        namespace
      );
      const updatedParams = typeof newParams === 'function' ? newParams(currentParams) : newParams;

      Object.entries(updatedParams).forEach(([key, value]) => {
        const scopedKey = namespace ? `${namespace}:${key}` : key;
        if (value === undefined || value === null || (Array.isArray(value) && value.length === 0)) {
          params.delete(scopedKey);
          return;
        }
        const encodedVal = btoa(encodeURIComponent(JSON.stringify(value)));
        params.set(scopedKey, encodedVal);
      });

      // Replace the current entry (no extra history record) while keeping the route.
      history.replace({ pathname, hash, search: params.toString() });
    },
    [history, pathname, hash, namespace]
  );

  return [formattedSearchParams, setSearchParams] as const;
};
